// NP-4 Step 5 — how the Save dialog reacts when the server refuses a save.
//
// A normal update sends `baseRevision` (NP-4 Step 4). The server answers:
//   409 {"detail": {"code": "revision_conflict", …}} — the record was saved
//       somewhere else (another tab or device) after this copy was opened;
//   404 {"detail": {"code": "not_found"}} — the record no longer exists;
//   428 {"detail": {"code": "revision_required"}} — (NP-4 Step 7) the save
//       did not say which version it was based on, so the server cannot
//       confirm it is the latest.
// Nothing was written in any case. The coach's work must never be lost
// and never silently overwrite newer work, so:
//   • conflict / unconfirmed → the dialog stays open with exactly two
//     choices: Save as copy (the existing Save As New flow) or Cancel;
//   • missing  → an error message; nothing is recreated automatically.
// Anything else keeps the dialog's existing error handling.
//
// Pure: no React, so the rules are unit-tested (saveConflict.test.js).

const detailCode = (error) => {
  const d = error && error.response && error.response.data && error.response.data.detail;
  return d && typeof d === "object" ? d.code : undefined;
};

// "conflict" | "unconfirmed" | "missing" | null (null = not a concurrency refusal).
export function saveRefusal(error) {
  const status = error && error.response && error.response.status;
  if (status === 409 && detailCode(error) === "revision_conflict") return "conflict";
  if (status === 428 && detailCode(error) === "revision_required") return "unconfirmed";
  if (status === 404 && detailCode(error) === "not_found") return "missing";
  return null;
}

// Name pre-filled in the Save As New field when the coach chooses
// "Save as copy" — the same "<name> Copy" convention as Save As New.
export function copyName(name) {
  const base = (name || "").trim();
  return base ? `${base} Copy` : "";
}

export const conflictText = (kind) => ({
  title: `This ${kind} has changed`,
  message: `This ${kind} has changed since you opened it. Your version was not saved.`,
  detail: "It was saved somewhere else — in another tab or on another device. Save your work as a copy to keep both versions.",
});

// NP-4 Step 7 — 428: the approved "can't confirm" wording.
export const unconfirmedText = (kind, name) => ({
  title: "Can't confirm this is the latest version",
  message: `"${(name || "").trim() || `This ${kind}`}" may have been changed somewhere else since this work was last saved.`,
  detail: "To avoid replacing newer changes, save your work as a copy.",
});

// Panel wording for a refusal that offers Save as copy / Cancel.
export const refusalText = (refusal, kind, name) =>
  refusal === "unconfirmed" ? unconfirmedText(kind, name) : conflictText(kind);

export const missingMessage = (kind, name) =>
  `"${(name || "").trim() || `This ${kind}`}" can't be saved: it no longer exists (it may have been deleted in another tab or on another device). Your work here is unchanged — use Save As New to keep it.`;
